import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";

import { ApiService } from "../../../config";
import { handleToken } from "../../../utils/expiredTokenLogout";
import { handleError } from "../../../utils/parseApiReturn";
import type { ContaConectada, EstadoDeAcesso, PerfilQualidade, QualidadePorEmail } from "../types";

const auth = (token: string): AxiosRequestConfig => ({
  headers: { Authorization: `Bearer ${token}` },
});

const getQualidadePorEmail = async (token: string): Promise<QualidadePorEmail[]> => {
  try {
    const result = await ApiService.get("/profile-quality/by-email", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar a qualidade dos perfis.").message);
    return [];
  }
};

const getPerfis = async (
  token: string,
  filtros: { email?: string; estado?: EstadoDeAcesso },
): Promise<PerfilQualidade[]> => {
  try {
    const result = await ApiService.get("/profile-quality/profiles", {
      ...auth(token),
      params: filtros,
    });
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar os perfis.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar os perfis.").message);
    return [];
  }
};

const marcarAcessoSolicitado = async (token: string, publicId: string): Promise<boolean> => {
  try {
    const result = await ApiService.put(
      `/profile-quality/${publicId}/acesso-solicitado`,
      {},
      auth(token),
    );
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao marcar o pedido de acesso.").message);
      return false;
    }
    toast.success("Marcado como acesso solicitado.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao marcar o pedido de acesso.").message);
    return false;
  }
};

const getContas = async (token: string): Promise<ContaConectada[]> => {
  try {
    const result = await ApiService.get("/profile-quality/accounts", auth(token));
    const { data, error } = result.data;
    if (error) {
      handleToken(result.data.message);
      toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
      return [];
    }
    return data ?? [];
  } catch (error) {
    toast.error(handleError(error, "Erro ao carregar as contas conectadas.").message);
    return [];
  }
};

const verificarConvites = async (token: string, userId: number): Promise<string | null> => {
  try {
    const result = await ApiService.post(`/profile-quality/accounts/${userId}/invitations/refresh`, {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao consultar os convites.").message);
      return null;
    }
    return String(result.data?.data ?? "Convites verificados.");
  } catch (error) {
    toast.error(handleError(error, "Erro ao consultar os convites.").message);
    return null;
  }
};

const verificarTodosOsConvites = async (token: string): Promise<boolean> => {
  try {
    const result = await ApiService.post("/profile-quality/accounts/invitations/refresh-all", {}, auth(token));
    if (result.data?.error) {
      toast.error(handleError(result.data.error, "Erro ao iniciar a verificação.").message);
      return false;
    }
    toast.success("Verificação iniciada. Os convites aparecem aqui em alguns minutos.");
    return true;
  } catch (error) {
    toast.error(handleError(error, "Erro ao iniciar a verificação.").message);
    return false;
  }
};

export const profileQualityService = {
  getQualidadePorEmail,
  getPerfis,
  marcarAcessoSolicitado,
  getContas,
  verificarConvites,
  verificarTodosOsConvites,
};
